import { use, useRef, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "lib/i18n/use-translation";
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONTS,
  LINE_HEIGHT_MAX,
  LINE_HEIGHT_MIN,
  LINE_HEIGHT_STEP,
  MARGINS_MAX,
  MARGINS_MIN,
  SettingsContext,
} from "lib/settings/use-settings";
import { Overlay } from "lib/ui/overlay";
import { Slider } from "lib/ui/slider";
import { Toggle } from "lib/ui/toggle";
import { useOuterClick } from "lib/utils/use-outer-click";
import { useIsMobileWidth } from "lib/utils/viewport";

import ChevronIcon from "assets/icons/chevron-right.svg?react";
import GearIcon from "assets/icons/gear.svg?react";
import css from "./settings.module.scss";

export function Settings() {
  const [isOpen, setIsOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  useOuterClick(rootRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  function toggleOpen() {
    setIsOpen(!isOpen);
    setShowAdvanced(false);
  }

  return (
    <div ref={rootRef}>
      <button
        onClick={toggleOpen}
        data-testid="readerSettingsButton"
        className={clsx(css.trigger, isOpen && css.active)}
        title={t("Настройки")}
      >
        <GearIcon width={24} height={24} />
      </button>
      <Overlay
        open={isOpen}
        onClick={() => setIsOpen(false)}
        className={css.overlay}
      />
      <div className={clsx(css.popover, isOpen && css.open)}>
        <div className={css.themeButtonsSection}>
          <ThemeButtons />
        </div>

        <div className={css.mainSection}>
          <div className={css.basicSettings}>
            <FontFamily />
            <div className={css.divider} />
            <FontSize />
          </div>

          <div className={css.advancedSettings}>
            {!showAdvanced && (
              <>
                <div className={css.divider} />
                <button
                  className={css.showMoreButton}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  aria-expanded={showAdvanced}
                >
                  {t("Показать больше")}
                </button>
              </>
            )}

            {showAdvanced && (
              <>
                <Margins />
                <LineHeight />
                <JustifyToggle />
                <WordWrapToggle />
                <ColumnsToggle />
                <TurnPageOnClickToggle />
                <button
                  className={css.okButton}
                  onClick={() => setIsOpen(false)}
                  type="button"
                >
                  {t("Готово")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FontFamily() {
  const { font, setFont } = use(SettingsContext)!;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  useOuterClick(ref, () => setOpen(false));

  return (
    <div className={css.control} ref={ref}>
      <label htmlFor="font" className={css.label}>
        {t("Шрифт")}
      </label>
      <div
        className={css.dropdown}
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onBlur={() => setOpen(false)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={css.selectedFont} style={{ fontFamily: font }}>
          {font}
        </span>
        <ChevronIcon className={clsx(css.arrow, open && css.arrowOpen)} />
        {open && (
          <ul className={css.dropdownList} role="listbox">
            {FONTS.map(f => (
              <li
                key={f}
                className={clsx(css.dropdownItem, font === f && css.active)}
                style={{ fontFamily: f }}
                role="option"
                aria-selected={font === f}
                onClick={e => {
                  setFont(f);
                  setOpen(false);
                  e.stopPropagation();
                }}
              >
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FontSize() {
  const { fontSize, setFontSize } = use(SettingsContext)!;
  const min = FONT_SIZE_MIN;
  const max = FONT_SIZE_MAX;

  const { t } = useTranslation();

  return (
    <div className={css.control}>
      <label htmlFor="fontSize" className={css.label}>
        {t("Размер шрифта")}
      </label>
      <div className={css.fontSizeWrapper}>
        <input
          type="number"
          id="fontSize"
          value={fontSize}
          min={min}
          max={max}
          onChange={e => setFontSize(Number(e.target.value))}
          className={css.fontSizeInput}
          aria-label={t("Размер шрифта")}
        />
        <div className={css.fontSizeButtons}>
          <button
            type="button"
            className={css.fontSizeBtn}
            onClick={() => setFontSize(Math.max(min, fontSize - 1))}
            disabled={fontSize <= min}
            aria-label={t("Уменьшить размер шрифта")}
          >
            –
          </button>
          <button
            type="button"
            className={css.fontSizeBtn}
            onClick={() => setFontSize(Math.min(max, fontSize + 1))}
            disabled={fontSize >= max}
            aria-label={t("Увеличить размер шрифта")}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

function Margins() {
  const { margins, setMargins } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <div className={css.control}>
      <label htmlFor="margins" className={css.label}>
        {t("Поля")}
      </label>
      <div className={css.sliderContainer}>
        <Slider
          value={margins}
          min={MARGINS_MIN}
          max={MARGINS_MAX}
          onChange={setMargins}
          id="margins"
          leftLabel={t("Узкие")}
          rightLabel={t("Широкие")}
        />
      </div>
    </div>
  );
}

function LineHeight() {
  const { lineHeight, setLineHeight } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <div className={css.control}>
      <label htmlFor="lineHeight" className={css.label}>
        {t("Высота строк")}
      </label>
      <div className={css.sliderContainer}>
        <Slider
          value={lineHeight}
          min={LINE_HEIGHT_MIN}
          max={LINE_HEIGHT_MAX}
          onChange={setLineHeight}
          id="lineHeight"
          leftLabel={t("Маленькая")}
          rightLabel={t("Большая")}
          step={LINE_HEIGHT_STEP}
        />
      </div>
    </div>
  );
}

function ThemeButtons() {
  const { themeMode, setThemeMode } = use(SettingsContext)!;

  return (
    <div className={css.themeButtons}>
      <button
        className={clsx(
          css.themeButton,
          css.light,
          themeMode === "light" && css.active,
          "theme-light"
        )}
        aria-pressed={themeMode === "light"}
        onClick={() => setThemeMode("light")}
        type="button"
      >
        A
      </button>
      <button
        className={clsx(
          css.themeButton,
          css.sepia,
          themeMode === "sepia" && css.active,
          "theme-sepia"
        )}
        aria-pressed={themeMode === "sepia"}
        onClick={() => setThemeMode("sepia")}
        type="button"
      >
        A
      </button>
      <button
        className={clsx(
          css.themeButton,
          css.dark,
          themeMode === "dark" && css.active,
          "theme-dark"
        )}
        aria-pressed={themeMode === "dark"}
        onClick={() => setThemeMode("dark")}
        type="button"
      >
        A
      </button>
    </div>
  );
}

function JustifyToggle() {
  const { textAlignJustify, setTextAlignJustify } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <label className={css.control}>
      <span className={css.label}>{t("Выравнивание по ширине")}</span>
      <Toggle checked={textAlignJustify} onChange={setTextAlignJustify} />
    </label>
  );
}

function WordWrapToggle() {
  const { wordWrap, setWordWrap } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <label className={css.control}>
      <span className={css.label}>{t("Перенос слов")}</span>
      <Toggle checked={wordWrap} onChange={setWordWrap} />
    </label>
  );
}

function ColumnsToggle() {
  const { cols, setCols } = use(SettingsContext)!;
  const isMobile = useIsMobileWidth();
  const { t } = useTranslation();

  return (
    <label className={css.control}>
      <span className={css.label}>{t("Показывать текст в 2 колонки")}</span>
      <Toggle
        checked={cols === 2}
        onChange={checked => {
          setCols(checked ? 2 : 1);
        }}
        disabled={isMobile}
      />
    </label>
  );
}

function TurnPageOnClickToggle() {
  const { turnPageOnClick, setTurnPageOnClick } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <label className={css.control}>
      <span className={css.label}>{t("Листать по клику")}</span>
      <Toggle checked={turnPageOnClick} onChange={setTurnPageOnClick} />
    </label>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LayoutDirectionToggle() {
  const { layoutDirection, setLayoutDirection } = use(SettingsContext)!;
  const { t } = useTranslation();

  return (
    <label className={css.control}>
      <span className={css.label}>{t("Вертикальная прокрутка")}</span>
      <Toggle
        checked={layoutDirection === "vertical"}
        onChange={checked => {
          setLayoutDirection(checked ? "vertical" : "horizontal");
        }}
      />
    </label>
  );
}
